import React from 'react';
import { PrimaryThemeProvider } from '@components/blocks/BlockThemeProvider';
import LandingPageContent from '@components/landing/LandingPageContent';
import AppLayout from '@components/layout/AppLayout';
import SessionBanner from '@components/sessionDetails/SessionBanner';
import UserAlert from '@cvent/carina/components/Alert/UserAlert';
import useBreakpoints from '@hooks/useBreakpoints';
import { LandingPageProps } from '@server/getLandingPageProps';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useTranslate } from 'nucleus-text';
import getFeatureMetadata from '@tools/getFeatureMetadata';
import LandingNavigation from '../layout/LandingNavigation';
import FixedFooterWrapper from './FixedFooterWrapper';
import LandingPageCallToAction from './LandingPageCallToAction';
import { LOGIN_ERROR } from './landingUtils';

const FooterWithImage = dynamic(() => import('@components/layout/FooterWithImage'), {
  ssr: false
});

/**
 * Attendee Hub Landing Page
 * (pre-login)
 */
function LandingPage({
  eventInfo,
  onLandingPageLoginClick,
  loginUrl,
  signupLink,
  embeddedRegistrationUrl,
  environment,
  errorCode,
  reflexEmbeddedRegUrl
}: Omit<LandingPageProps, 'appProps'>): JSX.Element {
  const { translate } = useTranslate();
  const { isMobile, isMobileOrTablet } = useBreakpoints();

  const {
    id: eventId,
    eventInfoTheme: { coverImageOptimized, coverImageAltText, logoImageOptimized },
    showCookieBanner,
    showCookieBannerPrivacyPolicyLink,
    showNonEssentialCookieBanner,
    title,
    eventPrivacyPolicy,
    cventPrivacyPolicy,
    ccpaData: { ccpaExternalDoNotSellUrl: ccpa },
    hideCventFooter,
    faviconUrl,
    features
  } = eventInfo;

  const publicLandingPageFeatureMetadata = getFeatureMetadata(features, 'HAS_PUBLIC_LANDING_PAGE');

  const landingPageCallToAction = (
    <LandingPageCallToAction onLogin={onLandingPageLoginClick} size={isMobileOrTablet ? 's' : 'l'} />
  );

  const codeSnippets = eventInfo?.codeSnippets || [];
  const eventPrivacyLinks = eventPrivacyPolicy?.showPrivacyPolicy
    ? {
        url: eventPrivacyPolicy.privacyPolicyUrl,
        text: eventPrivacyPolicy.privacyPolicyLinkText
      }
    : undefined;
  const cventPrivacyPolicyText = cventPrivacyPolicy?.showCventPrivacyPolicy
    ? cventPrivacyPolicy.cventPrivacyPolicyLinkText
    : undefined;
  /* This is the lower level page layout that does not call Apollo queries (to keep load time low) */
  return (
    <>
      <Head>
        <title>{title}</title>
        <link key="favicon" rel="icon" href={faviconUrl || '/hub/favicon.ico'} />
      </Head>
      {logoImageOptimized && (
        <PrimaryThemeProvider>
          <LandingNavigation
            eventId={eventId}
            logoSrc={logoImageOptimized}
            useCustomLogoLink={!!publicLandingPageFeatureMetadata?.hideLoginButton}
          />
        </PrimaryThemeProvider>
      )}
      {coverImageOptimized && <SessionBanner imageSrc={coverImageOptimized} imageAltText={coverImageAltText} />}
      <AppLayout
        footer={
          !isMobile && (
            <FooterWithImage
              cventPrivacyPolicyText={cventPrivacyPolicyText}
              ccpaUrl={ccpa}
              eventPrivacyPolicy={eventPrivacyLinks}
              hideCventFooter={hideCventFooter}
              eventId={eventId}
              enableManageCookiePreferences={showCookieBanner && showNonEssentialCookieBanner}
              showNonEssentialCookieBanner={showNonEssentialCookieBanner}
              showCookieBanner={showCookieBanner}
              hidePrivacyPolicyLink={!showCookieBannerPrivacyPolicyLink}
              codeSnippets={codeSnippets}
            />
          )
        }
      >
        {errorCode === LOGIN_ERROR && (
          <UserAlert content={translate('Landing-Page-Alert-Login-Error')} appearance="danger" testID="landing-error" />
        )}
        <PrimaryThemeProvider>
          <LandingPageContent
            eventInfo={eventInfo}
            landingPageCallToAction={landingPageCallToAction}
            signupLink={signupLink}
            embeddedRegistrationUrl={embeddedRegistrationUrl}
            environment={environment}
            loginUrl={loginUrl}
            reflexEmbeddedRegUrl={reflexEmbeddedRegUrl}
            acceptAllCookies={showCookieBanner === false || showNonEssentialCookieBanner === false}
          />
        </PrimaryThemeProvider>
        {isMobile && (
          <FixedFooterWrapper>
            <FooterWithImage
              eventId={eventId}
              ccpaUrl={ccpa}
              hideCventFooter={hideCventFooter}
              showCookieBanner={showCookieBanner}
              eventPrivacyPolicy={eventPrivacyLinks}
              cventPrivacyPolicyText={cventPrivacyPolicyText}
              hidePrivacyPolicyLink={!showCookieBannerPrivacyPolicyLink}
              showNonEssentialCookieBanner={showNonEssentialCookieBanner}
              enableManageCookiePreferences={showCookieBanner && showNonEssentialCookieBanner}
              codeSnippets={codeSnippets}
            />
          </FixedFooterWrapper>
        )}
      </AppLayout>
    </>
  );
}

export default LandingPage;
